import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Clock, ChevronDown } from 'lucide-react';

interface TimePickerProps {
  startTime: string;
  endTime: string;
  allDay: boolean;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onAllDayChange: (allDay: boolean) => void;
}

export type DateFormat = 'DD/MM/YYYY' | 'MM/DD/YYYY';

// Hook to get and watch date format setting
export const useDateFormat = (): DateFormat => {
  const [dateFormat, setDateFormat] = useState<DateFormat>(() => {
    return (localStorage.getItem('calendar-date-format') as DateFormat) || 'MM/DD/YYYY';
  });
  useEffect(() => {
    const sync = () => {
      const f = localStorage.getItem('calendar-date-format') as DateFormat;
      if (f) setDateFormat(f);
    };
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    return () => { window.removeEventListener('storage', sync); window.removeEventListener('focus', sync); };
  }, []);
  return dateFormat;
};

// Format ISO date string as short display e.g. "Apr 20" or "20 Apr"
export const formatShortDate = (dateStr: string, format: DateFormat): string => {
  const [, mm, dd] = dateStr.split('-').map(Number);
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return format === 'DD/MM/YYYY' ? `${dd} ${MONTHS[mm - 1]}` : `${MONTHS[mm - 1]} ${dd}`;
};

// Format ISO date string as full numeric date e.g. "04/20/2026" or "20/04/2026"
export const formatFullDate = (dateStr: string, format: DateFormat): string => {
  const [y, m, d] = dateStr.split('-');
  return format === 'DD/MM/YYYY' ? `${d}/${m}/${y}` : `${m}/${d}/${y}`;
};

// Hook to get and watch time format setting
export const useTimeFormat = () => {
  const [timeFormat, setTimeFormat] = useState<'12h' | '24h'>(() => {
    return (localStorage.getItem('calendar-time-format') as '12h' | '24h') || '12h';
  });

  useEffect(() => {
    const handleChange = () => {
      const format = localStorage.getItem('calendar-time-format') as '12h' | '24h';
      if (format) setTimeFormat(format);
    };

    window.addEventListener('storage', handleChange);
    window.addEventListener('mozayc-time-format-changed', handleChange);

    return () => {
      window.removeEventListener('storage', handleChange);
      window.removeEventListener('mozayc-time-format-changed', handleChange);
    };
  }, []);

  return timeFormat;
};

// Helper to get current time rounded to nearest 15 minutes
export const getCurrentTimeRounded = (): string => {
  const now = new Date();
  const minutes = now.getMinutes();
  const roundedMinutes = Math.ceil(minutes / 15) * 15;
  const extraHour = roundedMinutes === 60 ? 1 : 0;
  const finalMinutes = roundedMinutes === 60 ? 0 : roundedMinutes;
  const finalHour = (now.getHours() + extraHour) % 24;
  return `${String(finalHour).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`;
};

// Helper to get default end time (1 hour after start)
export const getDefaultEndTime = (startTime: string): string => {
  const [hours, minutes] = startTime.split(':').map(Number);
  const endHour = (hours + 1) % 24;
  return `${String(endHour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

// Generate time options in 15-minute intervals for start time
export const generateStartTimeOptions = (timeFormat: '12h' | '24h') => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
      const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: timeFormat === '12h'
      });
      options.push({ value: time, label: displayTime });
    }
  }
  return options;
};

// Generate end time options relative to start time
// Shows common durations (30m, 1h, 1.5h, 2h) followed by all remaining times
const generateEndTimeOptions = (startTime: string, timeFormat: '12h' | '24h') => {
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const startTotalMinutes = startHours * 60 + startMinutes;

  const options = [];

  // First, add common duration shortcuts (30 min, 1 hr, 1.5 hr, 2 hr)
  const commonOffsets = [30, 60, 90, 120];
  for (const offset of commonOffsets) {
    const endTotalMinutes = startTotalMinutes + offset;
    const endHour = Math.floor(endTotalMinutes / 60) % 24;
    const endMinute = endTotalMinutes % 60;

    const time = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
    const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h'
    });

    const hours = Math.floor(offset / 60);
    const mins = offset % 60;
    let duration = '';
    if (hours > 0 && mins > 0) {
      duration = `${hours} hr ${mins} min`;
    } else if (hours > 0) {
      duration = `${hours} hr`;
    } else {
      duration = `${mins} min`;
    }

    options.push({ value: time, label: displayTime, duration });
  }

  // Then add all remaining times in 30-min intervals for the rest of the day
  for (let offset = 150; offset <= 23 * 60; offset += 30) {
    const endTotalMinutes = startTotalMinutes + offset;
    // Stop if we've wrapped around past 24 hours
    if (endTotalMinutes >= 24 * 60 + startTotalMinutes) break;

    const endHour = Math.floor(endTotalMinutes / 60) % 24;
    const endMinute = endTotalMinutes % 60;

    const time = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;
    const displayTime = new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h'
    });

    const hours = Math.floor(offset / 60);
    const mins = offset % 60;
    let duration = '';
    if (hours > 0 && mins > 0) {
      duration = `${hours} hr ${mins} min`;
    } else if (hours > 0) {
      duration = `${hours} hr`;
    } else {
      duration = `${mins} min`;
    }

    options.push({ value: time, label: displayTime, duration });
  }

  return options;
};

// Parse user input into 24h format (HH:MM) - accepts any minute value
const parseTimeInput = (input: string): string | null => {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return null;

  const patterns = [
    /^(\d{1,2}):(\d{2})$/,
    /^(\d{1,2})(\d{2})$/,
    /^(\d{1,2}):(\d{2})\s*(am|pm|a|p)$/i,
    /^(\d{1,2})\s*(am|pm|a|p)$/i,
    /^(\d{1,2})(\d{2})\s*(am|pm|a|p)$/i,
  ];

  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) {
      let hours = parseInt(match[1], 10);
      let minutes = match[2] && !match[2].match(/am|pm|a|p/i) ? parseInt(match[2], 10) : 0;
      const meridiem = match[3] || match[2]?.match(/am|pm|a|p/i)?.[0];

      if (meridiem) {
        const isPM = meridiem.toLowerCase().startsWith('p');
        const isAM = meridiem.toLowerCase().startsWith('a');

        if (hours === 12 && isAM) {
          hours = 0;
        } else if (hours !== 12 && isPM) {
          hours += 12;
        }
      }

      if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      }
    }
  }

  return null;
};

// Format 24h time to display format
const formatTimeDisplay = (time: string, timeFormat: '12h' | '24h' = '12h'): string => {
  try {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: timeFormat === '12h'
    });
  } catch {
    return time;
  }
};

// Calculate duration between two times
const calculateDuration = (start: string, end: string): string => {
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);

  let startTotal = startH * 60 + startM;
  let endTotal = endH * 60 + endM;

  if (endTotal <= startTotal) {
    endTotal += 24 * 60;
  }

  const diff = endTotal - startTotal;
  const hours = Math.floor(diff / 60);
  const mins = diff % 60;

  if (hours > 0 && mins > 0) {
    return `${hours} hr ${mins} min`;
  } else if (hours > 0) {
    return `${hours} hr`;
  } else {
    return `${mins} min`;
  }
};

interface StartTimeComboboxProps {
  value: string;
  onChange: (time: string) => void;
  options: { value: string; label: string }[];
  label: string;
  timeFormat: '12h' | '24h';
}

export const StartTimeCombobox: React.FC<StartTimeComboboxProps> = ({ value, onChange, options, label, timeFormat }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatTimeDisplay(value, timeFormat));
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) {
      setInputValue(formatTimeDisplay(value, timeFormat));
    }
  }, [value, isOpen, timeFormat]);

  const filteredOptions = inputValue === formatTimeDisplay(value)
    ? options
    : options.filter(opt =>
      opt.label.toLowerCase().includes(inputValue.toLowerCase()) ||
      opt.value.includes(inputValue.replace(/[^0-9:]/g, ''))
    );

  const handleBlur = useCallback(() => {
    const parsed = parseTimeInput(inputValue);
    if (parsed) {
      onChange(parsed);
      setInputValue(formatTimeDisplay(parsed, timeFormat));
    } else {
      setInputValue(formatTimeDisplay(value, timeFormat));
    }
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, [inputValue, onChange, value, timeFormat]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleBlur();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleBlur]);

  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => {
    if (isOpen && listRef.current) {
      const currentIndex = filteredOptions.findIndex(opt => opt.value === value);
      if (currentIndex !== -1) {
        const itemHeight = 36;
        listRef.current.scrollTop = Math.max(0, (currentIndex - 2) * itemHeight);
      }
    }
  }, [isOpen, filteredOptions, value]);

  const handleSelect = (time: string) => {
    onChange(time);
    setInputValue(formatTimeDisplay(time, timeFormat));
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(0);
        } else {
          setHighlightedIndex(prev =>
            prev < filteredOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        } else {
          handleBlur();
          inputRef.current?.blur();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setInputValue(formatTimeDisplay(value, timeFormat));
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      case 'Tab':
        handleBlur();
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {label && <label className="block text-xs text-[var(--content-secondary)] mb-0.5">{label}</label>}
      <div className="relative">
        <Clock className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-[var(--content-tertiary)] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => {
            setIsOpen(true);
            setInputValue('');
          }}
          onKeyDown={handleKeyDown}
          className="w-full pl-7 pr-7 py-1.5 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--content-primary)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] text-xs"
          placeholder={formatTimeDisplay(value, timeFormat)}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
          className="absolute right-1.5 top-1/2 transform -translate-y-1/2 p-0.5 hover:bg-[var(--sidebar-hover)] rounded"
        >
          <ChevronDown className={`w-3.5 h-3.5 text-[var(--content-tertiary)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-lg shadow-lg overflow-y-auto"
          style={{ maxHeight: '256px' }}
        >
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <div
                key={option.value}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelect(option.value);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`h-8 px-2.5 flex items-center cursor-pointer text-xs ${index === highlightedIndex
                  ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)]'
                  : option.value === value
                    ? 'bg-[var(--surface-tertiary)] text-[var(--content-primary)] font-medium'
                    : 'text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)]'
                  }`}
              >
                {option.label}
              </div>
            ))
          ) : (
            <div className="h-8 px-2.5 flex items-center text-xs text-[var(--content-secondary)]">
              Type any time (e.g., 2:15pm)
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface EndTimeComboboxProps {
  value: string;
  startTime: string;
  onChange: (time: string) => void;
  label: string;
  timeFormat: '12h' | '24h';
}

export const EndTimeCombobox: React.FC<EndTimeComboboxProps> = ({ value, startTime, onChange, label, timeFormat }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(formatTimeDisplay(value, timeFormat));
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const endTimeOptions = generateEndTimeOptions(startTime, timeFormat);
  const currentDuration = calculateDuration(startTime, value);

  useEffect(() => {
    if (!isOpen) {
      setInputValue(formatTimeDisplay(value, timeFormat));
    }
  }, [value, isOpen, timeFormat]);

  const handleBlur = useCallback(() => {
    const parsed = parseTimeInput(inputValue);
    if (parsed) {
      onChange(parsed);
      setInputValue(formatTimeDisplay(parsed, timeFormat));
    } else {
      setInputValue(formatTimeDisplay(value, timeFormat));
    }
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, [inputValue, onChange, value, timeFormat]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        handleBlur();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleBlur]);

  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.children;
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (time: string) => {
    onChange(time);
    setInputValue(formatTimeDisplay(time, timeFormat));
    setIsOpen(false);
    setHighlightedIndex(-1);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setHighlightedIndex(0);
        } else {
          setHighlightedIndex(prev =>
            prev < endTimeOptions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndex >= 0 && endTimeOptions[highlightedIndex]) {
          handleSelect(endTimeOptions[highlightedIndex].value);
        } else {
          handleBlur();
          inputRef.current?.blur();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setInputValue(formatTimeDisplay(value, timeFormat));
        setIsOpen(false);
        inputRef.current?.blur();
        break;
      case 'Tab':
        handleBlur();
        break;
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {label && <label className="block text-xs text-[var(--content-secondary)] mb-0.5">{label}</label>}
      <div className="relative">
        <Clock className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-[var(--content-tertiary)] pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setIsOpen(true);
            setHighlightedIndex(0);
          }}
          onFocus={() => {
            setIsOpen(true);
            setInputValue('');
          }}
          onKeyDown={handleKeyDown}
          className="w-full pl-7 pr-7 py-1.5 border border-[var(--input-border)] bg-[var(--input-bg)] text-[var(--content-primary)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] text-xs"
          placeholder={formatTimeDisplay(value, timeFormat)}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={() => {
            setIsOpen(!isOpen);
            inputRef.current?.focus();
          }}
          className="absolute right-1.5 top-1/2 transform -translate-y-1/2 p-0.5 hover:bg-[var(--sidebar-hover)] rounded"
        >
          <ChevronDown className={`w-3.5 h-3.5 text-[var(--content-tertiary)] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {isOpen && (
        <div
          ref={listRef}
          className="absolute z-50 w-full mt-1 bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-lg shadow-lg overflow-y-auto"
          style={{ maxHeight: '256px' }}
        >
          {endTimeOptions.map((option, index) => (
            <div
              key={option.value}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(option.value);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={`h-8 px-2.5 flex items-center justify-between cursor-pointer text-xs ${index === highlightedIndex
                ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)]'
                : option.value === value
                  ? 'bg-[var(--surface-tertiary)] text-[var(--content-primary)] font-medium'
                  : 'text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)]'
                }`}
            >
              <span>{option.label} <span className="text-[var(--content-tertiary)] text-xs">({option.duration})</span></span>
            </div>
          ))}
          <div className="h-8 px-2.5 flex items-center text-xs text-[var(--content-secondary)] border-t border-[var(--border-secondary)]">
            Type any time (e.g., 5:30pm)
          </div>
        </div>
      )}
    </div>
  );
};

export default function TimePicker({
  startTime,
  endTime,
  allDay,
  onStartTimeChange,
  onEndTimeChange,
  onAllDayChange
}: TimePickerProps) {
  const timeFormat = useTimeFormat();
  const startTimeOptions = generateStartTimeOptions(timeFormat);

  const handleStartTimeChange = (newStartTime: string) => {
    // Only update end time if start time actually changed
    if (newStartTime === startTime) return;

    onStartTimeChange(newStartTime);

    // Calculate new end time maintaining the same duration, or default to 1 hour
    const [oldStartH, oldStartM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    const [newStartH, newStartM] = newStartTime.split(':').map(Number);

    const oldStartMinutes = oldStartH * 60 + oldStartM;
    const endMinutes = endH * 60 + endM;
    const newStartMinutes = newStartH * 60 + newStartM;

    // Calculate duration (handle overnight events)
    let duration = endMinutes - oldStartMinutes;
    if (duration <= 0) duration += 24 * 60;

    // Apply same duration to new start time
    const newEndMinutes = (newStartMinutes + duration) % (24 * 60);
    const newEndH = Math.floor(newEndMinutes / 60);
    const newEndM = newEndMinutes % 60;

    onEndTimeChange(`${newEndH.toString().padStart(2, '0')}:${newEndM.toString().padStart(2, '0')}`);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={allDay}
          onChange={(e) => onAllDayChange(e.target.checked)}
          className="rounded border-[var(--input-border)]"
          id="all-day-checkbox"
        />
        <label htmlFor="all-day-checkbox" className="text-sm font-medium text-[var(--content-primary)] pointer-events-none">
          All day
        </label>
      </div>

      {!allDay && (
        <div className="grid grid-cols-2 gap-3">
          <StartTimeCombobox
            label="Start time"
            value={startTime}
            onChange={handleStartTimeChange}
            options={startTimeOptions}
            timeFormat={timeFormat}
          />
          <EndTimeCombobox
            label="End time"
            value={endTime}
            startTime={startTime}
            onChange={onEndTimeChange}
            timeFormat={timeFormat}
          />
        </div>
      )}
    </div>
  );
}